/**
 * ModelColumn Component
 * Feature 014: Unified Multi-AI Post Generation Flow
 * T044: Single model view with 5-section post structure
 */

import { memo } from 'react';
import { AlertCircle, CheckCircle } from 'lucide-react';
import type { IModelPostData, ModelKey } from '../../@types/posts';
import { Button } from '../ui/button';

interface IModelColumnProps {
  modelKey: 'claude' | 'gemini' | 'openai';
  modelName: string;
  postData: IModelPostData | undefined;
  status: 'idle' | 'loading' | 'completed' | 'error';
  selectedVariantModel: ModelKey | null; // NEW
  isSelecting: boolean; // NEW
  onSelectBestVariant: (modelKey: ModelKey) => void; // NEW
}

const MODEL_COLORS = {
  claude: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-700',
    badge: 'bg-purple-100',
  },
  gemini: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    badge: 'bg-blue-100',
  },
  openai: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    badge: 'bg-green-100',
  },
};

const ModelColumn = ({
  modelKey,
  modelName,
  postData,
  status,
  selectedVariantModel,
  isSelecting,
  onSelectBestVariant,
}: IModelColumnProps) => {
  const colors = MODEL_COLORS[modelKey];
  const isSelected = selectedVariantModel === modelKey;
  const canSelect = status === 'completed' && !postData?.error;

  // Parse sections from JSON content
  let sections: {
    Hook?: string;
    Problem_Setup?: string;
    Educational_Content?: string;
    Authority_Statement?: string;
    Call_to_Action?: string;
  } | null = null;

  if (postData?.content) {
    try {
      sections = JSON.parse(postData.content);
    } catch {
      // Parsing failed - will show error
    }
  }

  // Helper function to combine all sections into continuous text (Feature 015)
  const combineAllSections = (sectionData: typeof sections): string => {
    if (!sectionData) return '';

    const parts = [
      sectionData.Hook,
      sectionData.Problem_Setup,
      sectionData.Educational_Content,
      sectionData.Authority_Statement,
      sectionData.Call_to_Action,
    ].filter((s): s is string => s !== null && s !== undefined && s.trim().length > 0);

    return parts.join('\n\n');
  };

  return (
    <div
      className={`rounded-lg border ${colors.border} ${colors.bg} p-6 ${isSelected ? 'ring-2 ring-green-500' : ''}`}
    >
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h3 className={`text-lg font-semibold ${colors.text}`}>{modelName}</h3>
        <div className="flex items-center gap-2">
          {status === 'completed' && !postData?.error && (
            <CheckCircle className="h-5 w-5 text-green-600" />
          )}
          {status === 'loading' && (
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          )}
          {postData?.cached && (
            <span
              className={`rounded-full ${colors.badge} px-2 py-1 text-xs font-medium ${colors.text}`}
            >
              Cached
            </span>
          )}
          {isSelected && (
            <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
              Selected
            </span>
          )}
        </div>
      </div>

      {/* Error State */}
      {(status === 'error' || postData?.error) && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600" />
            <div>
              <h4 className="font-medium text-red-900">Generation Failed</h4>
              <p className="mt-1 text-sm text-red-700">
                {postData?.error || 'An error occurred during generation'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {status === 'loading' && (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(skeleton => (
            <div key={`skeleton-section-${skeleton}`} className="space-y-2">
              <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
              <div className="h-16 animate-pulse rounded bg-white" />
            </div>
          ))}
        </div>
      )}

      {/* Content - Combined Text (Feature 015: No section labels) */}
      {status === 'completed' && sections && !postData?.error && (
        <div className="space-y-4">
          {/* Combined text display - no section subtitles */}
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <p className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
              {combineAllSections(sections)}
            </p>
          </div>

          {/* Use This Version Button */}
          <div className="mt-6">
            <Button
              onClick={() => onSelectBestVariant(modelKey)}
              disabled={!canSelect || isSelecting}
              className="w-full"
              variant={isSelected ? 'outline' : 'default'}
            >
              {isSelecting && selectedVariantModel === modelKey
                ? 'Saving...'
                : isSelected
                  ? '✓ Selected - Click to Re-select'
                  : 'Use This Version'}
            </Button>
          </div>
        </div>
      )}

      {/* Idle State */}
      {status === 'idle' && (
        <div className="text-center text-sm text-gray-500">Waiting to start...</div>
      )}
    </div>
  );
};

export const ModelColumnMemo = memo(ModelColumn);
